import { PricingType, PricingTypes } from "../../../../common/constants"
import { Transaction } from "../../../../domain/invoicing/transaction"
import { Workarea } from "../../../../domain/job"
import { WorkareaGroup, WorkareaGroupWithWorkareas } from "../../../../domain/workareas/group"
import { WorkareaQuotedPriceChange } from "../../../../domain/workareas/groupsApi"
import { sum } from "../../../../helpers/array"
import { roundToPrecision } from "../../../../helpers/math"
import { hasValue } from "../../../../helpers/nullable"
import { WorkareaSummary } from "../../../Items/itemSummaryModel"
import { arePricingFieldsLocked } from "../EditorDrawer/workareaEditorModel"

export type State = {
  workareas: GroupedWorkareas
  workareaGroups: WorkareaGroup[]
}

export type GroupedWorkareas = Record<string, Workarea[]>

export const defaultWorkareaGroup = (): WorkareaGroup => {
  const defaultGroup: WorkareaGroup = {
    id: 'workareaGroup-0',
    name: 'New workarea group',
    pricingType: 0,
    customerAndInvoiceNotes: 'New workarea group',
    invoiceDescription: null,
    defaultInvoiceDescription: null,
    sortIndex: 0,
    isApproved: false,
    isOnHold: false,
    isHidden: false,
    approvedAt: "",
    approvedByCustomerEmail: "",
    approvedByName: "",
    approvedByUserEmail: "",
    approvedWithIpAddress: "",
    invoiceGroupingOption: 0,
    jobId: 0,
    qboExemptSalesTaxId: 0,
    qboSalesTaxId: 0,
    showTaxInFinalPrice: false,
    taxRateId: null,
    tenantId: 0,
  }
  return defaultGroup
}

export const buildGroupedWorkareas = (workareaGroups: WorkareaGroupWithWorkareas[]): GroupedWorkareas => {
  return Object.fromEntries(workareaGroups.map(x => {
    const workareas = x.workareas ?? []
    workareas.sort((a, b) => a.sortIndex - b.sortIndex)
    return [x.id, workareas]
  }))
}

export const removeFromAllGroups = (workareas: GroupedWorkareas, workareaId: number): GroupedWorkareas => {
  return Object.fromEntries(Object.entries(workareas).map(([groupId, groupWorkareas]) => [groupId, groupWorkareas.filter(w => w.id !== workareaId)]))
}

export const setGroupSortIndex = (state: State, groupId: string, index: number): State => {
  const workareaGroups = state.workareaGroups.map(group => group.id === groupId ? { ...group, sortIndex: index } : group)
  workareaGroups.sort((a, b) => a.sortIndex - b.sortIndex)
  return {
    ...state,
    workareaGroups
  }
}

export const setWorkareaSortIndex = (state: State, groupId: string, workareaId: number, sortIndex: number): State => {
  const workarea = Object.values(state.workareas).flat().find(w => w.id === workareaId)!
  const workareas = removeFromAllGroups(state.workareas, workareaId)
  workareas[groupId] = [...workareas[groupId], { ...workarea, workareaGroupId: groupId, sortIndex }]
  workareas[groupId].sort((a, b) => a.sortIndex - b.sortIndex)
  return {
    ...state,
    workareas
  }
}

export const updateGroup = (group: WorkareaGroup, updated: WorkareaGroup): WorkareaGroup => {
  return {
    ...group,
    name: updated.name,
    pricingType: updated.pricingType,
    customerAndInvoiceNotes: updated.customerAndInvoiceNotes
  }
}

export const updateWorkareaGroupSortIndex = (groups: WorkareaGroupWithWorkareas[], sortedGroupIds: string[]): WorkareaGroupWithWorkareas[] => {
  return groups.map(group => {
    const newIndex = sortedGroupIds.indexOf(group.id)
    return { ...group, sortIndex: newIndex < 0 ? group.sortIndex : newIndex }
  })
}

export const updateWorkareaSortIndex = (workareaGroups: WorkareaGroupWithWorkareas[], workareaSorting: Record<string, number[]>): WorkareaGroupWithWorkareas[] => {
  return workareaGroups.map(group => {
    const workareas = group.workareas.map(workarea => {
      const newSortIndex = workareaSorting[group.id].indexOf(workarea.id)
      return { ...workarea, sortIndex: newSortIndex }
    })
    return { ...group, workareas }
  })
}

export type WorkareaGroupMoveWarnings = {
  showWarning: boolean
  pricingTypeWarning: boolean
  recurringWorkareaCount: number
  originalGroup: WorkareaGroup
  destinationGroup?: WorkareaGroup
  originalGroupHasInvoices: boolean
  destinationGroupHasInvoices: boolean
}

export const checkForWorkareaMoveWarnings = (workareaId: number, destinationGroupId: string, state: State, transactions: Transaction[]): WorkareaGroupMoveWarnings => {
  const workarea = Object.values(state.workareas).flat().find(w => w.id === workareaId)
  const originalGroup = state.workareaGroups.find(g => g.id === workarea?.workareaGroupId)
  const destinationGroup = state.workareaGroups.find(g => g.id === destinationGroupId)

  if (!hasValue(workarea)) {
    throw new Error(`Workarea not found: ${workareaId}`)
  }

  if (!hasValue(originalGroup)) {
    throw new Error(`Group not found: ${workarea.workareaGroupId}`)
  }

  const originalGroupHasInvoices = doesGroupHaveInvoices(originalGroup.id, state, transactions)
  const destinationGroupHasInvoices = hasValue(destinationGroup) ? doesGroupHaveInvoices(destinationGroup.id, state, transactions) : false

  let pricingTypeWarning = false

  if (destinationGroup && originalGroup.pricingType !== destinationGroup.pricingType) {
    pricingTypeWarning = true
  }

  const masterWorkareaId = hasValue(workarea?.masterWorkareaId) ? workarea?.masterWorkareaId : workarea?.id
  const relatedWorkareas = hasValue(masterWorkareaId)
    ? Object.values(state.workareas).flat().filter(w => w.id === masterWorkareaId || w.masterWorkareaId === masterWorkareaId)
    : []

  return {
    showWarning: pricingTypeWarning || relatedWorkareas.length > 1 || originalGroupHasInvoices || destinationGroupHasInvoices || originalGroup.isApproved || (destinationGroup?.isApproved ?? false),
    pricingTypeWarning,
    recurringWorkareaCount: relatedWorkareas.length,
    originalGroup,
    destinationGroup,
    originalGroupHasInvoices,
    destinationGroupHasInvoices
  }
}

const doesGroupHaveInvoices = (groupId: string, state: State, transactions: Transaction[]): boolean => {
  return state.workareas[groupId].some(workarea => transactions.flatMap(t => t.elements).some(e => e.workareaId === workarea.id && e.amount > 0))
}

export const countChildRecurringWorkareas = (workareas: GroupedWorkareas | undefined, masterWorkareaId: number | undefined): number => {
  if (workareas === undefined) {
    return 0
  }

  const relatedWorkareas = hasValue(masterWorkareaId)
    ? Object.values(workareas).flat().filter(w => w.masterWorkareaId === masterWorkareaId)
    : []

  return relatedWorkareas.length
}

export const getChildrenWorkareasFromGroupedWorkareas = (groupId: string, masterWorkareaId: number, groupedWorkareas?: GroupedWorkareas): Workarea[] => {
  if (!groupedWorkareas) return [];

  return groupedWorkareas?.[groupId]?.filter(child => child.masterWorkareaId === masterWorkareaId) || [];
}

export const filterWorkareaGroups = (
  groupsWithWorkareas: WorkareaGroupWithWorkareas[],
  pricingType: PricingType[],
  searchTerm?: string
): WorkareaGroupWithWorkareas[] => {

  if (!searchTerm && pricingType.length === 0) {
    return groupsWithWorkareas;
  }

  let filteredGroups = groupsWithWorkareas;
  if (pricingType.length > 0) {
    filteredGroups = filteredGroups.filter(group => pricingType.includes(group.pricingType));
  }

  if (searchTerm) {
    const lowerCasedSearchTerm = searchTerm.toLowerCase();
    filteredGroups = filteredGroups.filter(group =>
      group.name.toLowerCase().includes(lowerCasedSearchTerm) ||
      group.workareas.some(workarea => workarea.name.toLowerCase().includes(lowerCasedSearchTerm))
    );
  }

  return filteredGroups;
};

export function filterFirstOfEachPricingType(workareaGroups: WorkareaGroupWithWorkareas[]): WorkareaGroupWithWorkareas[] {
  const pricingTypeMap: { [key: number]: boolean } = {};
  const filteredWorkareaGroups: WorkareaGroupWithWorkareas[] = [];

  for (const group of workareaGroups) {
    if (!pricingTypeMap[group.pricingType]) {
      pricingTypeMap[group.pricingType] = true;
      filteredWorkareaGroups.push(group);
    }
  }

  return filteredWorkareaGroups;
}

export type OverviewData = {
  estimatedHours: number
  estimatedPrice: number
  breakEven: number
  netProfit: number
  netProfitPercentage: number
  quotedTotal: number
  timeAndMaterialsTotal: number
  perVisitTotal: number
  quotedHours: number
  timeAndMaterialsHours: number
  perVisitHours: number
  finalPrice: number
  total: number
}

export const getOverviewData = (workareaSummaries: WorkareaSummary[]): OverviewData => {
  const notOnHoldSummaries = workareaSummaries.filter(x => !x.isOnHold)
  const estimatedHours = roundToPrecision(sum(notOnHoldSummaries, x => x.estimatedHours), 1)
  const estimatedPrice = sum(notOnHoldSummaries, x => x.estimatedPrice)
  const breakEven = sum(notOnHoldSummaries, x => x.estimatedBreakEven)
  const netProfit = sum(notOnHoldSummaries, x => x.estimatedNetProfit)
  
  const quotedTotal = sum(notOnHoldSummaries.filter(x => x.pricingType === PricingTypes.QuotedPrice), x => x.total)
  const timeAndMaterialsTotal = sum(notOnHoldSummaries.filter(x => x.pricingType === PricingTypes.TimeAndMaterials), x => x.total)
  const perVisitTotal = sum(notOnHoldSummaries.filter(x => x.pricingType === PricingTypes.PerVisit), x => x.total)
  const finalPrice = sum(notOnHoldSummaries, x => x.finalPrice)
  const total = sum(notOnHoldSummaries, x => x.total)

  const quotedHours = sum(notOnHoldSummaries.filter(x => x.pricingType === PricingTypes.QuotedPrice), x => x.estimatedHours)
  const timeAndMaterialsHours = sum(notOnHoldSummaries.filter(x => x.pricingType === PricingTypes.TimeAndMaterials), x => x.estimatedHours)
  const perVisitHours = sum(notOnHoldSummaries.filter(x => x.pricingType === PricingTypes.PerVisit), x => x.estimatedHours)

  const totalWorkPrice = sum(notOnHoldSummaries, x => x.total - x.recovery)
  const netProfitPercentage = totalWorkPrice === 0 ? 0 : (netProfit / totalWorkPrice)

  return {
    estimatedHours,
    estimatedPrice,
    breakEven,
    netProfit,
    netProfitPercentage: roundToPrecision(netProfitPercentage, 2),
    quotedTotal,
    timeAndMaterialsTotal,
    perVisitTotal,
    quotedHours,
    timeAndMaterialsHours,
    perVisitHours,
    finalPrice,
    total
  }
}

export type FinalPriceChangesOnNotApproved = {
  workareaId: number
  workareaName: string
  currentQuotedPrice: number
  estimatedPrice: number
}

export const getFinalPriceChangesOnNotApproved = (group: WorkareaGroupWithWorkareas, workareaSummaries: WorkareaSummary[]): FinalPriceChangesOnNotApproved[] => {
  if (!group.isApproved || group.pricingType !== PricingTypes.QuotedPrice) {
    return []
  }

  return group.workareas
    .filter(x => !hasValue(x.customQuotedPrice))
    .map(workarea => {
      const summary = workareaSummaries.find(x => x.workareaId === workarea.id)
      return {
        workareaId: workarea.id,
        workareaName: workarea.name,
        currentQuotedPrice: workarea.quotedPrice ?? summary?.estimatedPrice ?? 0,
        estimatedPrice: summary?.estimatedPrice ?? 0
      }
    })
    .filter(x => x.currentQuotedPrice !== x.estimatedPrice)
}

export const arePricingFieldsLockedForGroup = (group: WorkareaGroupWithWorkareas, transactions: Transaction[]): boolean => {
  return group.workareas.some(w => arePricingFieldsLocked(transactions, w.id))
}

export const mapFinalPriceChangesToRequestFormat = (changes: FinalPriceChangesOnNotApproved[], keepFinalPrices: boolean): WorkareaQuotedPriceChange[] => {
  return changes.map((x) => ({
    workareaId: x.workareaId,
    quotedPrice: keepFinalPrices ? x.currentQuotedPrice : x.estimatedPrice,
    setCustomQuotedPrice: keepFinalPrices
  }))
}

export const getAlreadyInvoicedAmountForWorkarea = (workareaId: number, transactions: Transaction[]): number => {
  return sum(transactions.flatMap(t => t.elements).filter(e => e.workareaId === workareaId), x => x.amount)
}
